import React from 'react';
import { Copy, FileText, Folder, Mail } from 'lucide-react';
import { Modal, ModalContent, ModalFooter } from '../../../components/ui/Modal';
import { Button } from '../../../components/ui';
import { SpinnerIcon } from '../../../components/ui/Icons';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import { Z_INDEX } from '../../../lib/zLayers';

export interface TemplateFolderCopyConfirmModalProps {
  isOpen: boolean;
  folderName: string;
  targetFolderName: string;
  itemsToCopy: string[];
  copying?: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

const TemplateFolderCopyConfirmModal: React.FC<TemplateFolderCopyConfirmModalProps> = ({
  isOpen,
  folderName,
  targetFolderName,
  itemsToCopy,
  copying = false,
  onClose,
  onConfirm,
}) => {
  const { colors } = useTheme();

  if (!isOpen) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => !copying && onClose()}
      size="md"
      zIndex={Z_INDEX.modalHigh}
      closeOnEscape={!copying}
    >
      <ModalContent>
        <div className="space-y-5 pt-1">
          <div className="flex flex-col items-center text-center sm:flex-row sm:items-start sm:text-left gap-4">
            <div
              className={cn(
                'flex h-12 w-12 shrink-0 items-center justify-center rounded-full',
                'bg-indigo-100 text-indigo-600 dark:bg-indigo-950/60 dark:text-indigo-400',
              )}
            >
              <Copy className="h-6 w-6" aria-hidden />
            </div>
            <div className="min-w-0 flex-1">
              <h3 className={cn('text-lg font-semibold tracking-tight', colors.text)}>
                Copy this folder?
              </h3>
              <p className={cn('mt-1 text-sm break-words', colors.textSecondary)}>
                A full copy of
              </p>
              <p
                className={cn(
                  'mt-1 text-sm font-semibold break-words',
                  'text-indigo-800 dark:text-indigo-200',
                )}
                title={folderName}
              >
                {folderName}
              </p>
              <p className={cn('mt-2 text-sm break-words', colors.textSecondary)}>
                will be created inside
              </p>
              <p
                className={cn(
                  'mt-1 text-sm font-semibold break-words',
                  'text-emerald-800 dark:text-emerald-200',
                )}
                title={targetFolderName}
              >
                {targetFolderName}
              </p>
            </div>
          </div>

          {itemsToCopy.length > 0 ? (
            <div
              className={cn(
                'rounded-xl border px-4 py-3',
                colors.border,
                'bg-gray-50/90 dark:bg-gray-900/40',
              )}
            >
              <p className={cn('text-xs font-semibold uppercase tracking-wider mb-2.5', colors.textMuted)}>
                What will be copied
              </p>
              <ul className="space-y-2">
                {itemsToCopy.map((item) => {
                  const isSubfolder = item.includes('subfolder');
                  const isDocument = item.includes('document');
                  const Icon = isSubfolder ? Folder : isDocument ? FileText : Mail;
                  return (
                    <li
                      key={item}
                      className={cn('flex items-center gap-2.5 text-sm font-medium', colors.text)}
                    >
                      <Icon
                        className={cn(
                          'h-4 w-4 shrink-0',
                          isSubfolder
                            ? 'text-amber-600 dark:text-amber-400'
                            : isDocument
                              ? 'text-blue-600 dark:text-blue-400'
                              : 'text-indigo-600 dark:text-indigo-400',
                        )}
                        aria-hidden
                      />
                      <span>{item}</span>
                    </li>
                  );
                })}
              </ul>
            </div>
          ) : (
            <p className={cn('text-sm', colors.textMuted)}>This folder is empty and will be duplicated.</p>
          )}

          <p className={cn('text-sm leading-relaxed', colors.textMuted)}>
            The original folder and its contents will not be changed. Nested folders, templates,
            documents, and attachments will all be included in the copy.
          </p>
        </div>
      </ModalContent>
      <ModalFooter>
        <div className="flex w-full flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={copying}
            className="w-full sm:w-auto"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={onConfirm}
            disabled={copying}
            className={cn(
              'w-full sm:w-auto font-semibold',
              'bg-indigo-600 text-white hover:bg-indigo-700',
              'focus:ring-indigo-500 dark:bg-indigo-600 dark:hover:bg-indigo-500',
            )}
          >
            {copying ? (
              <>
                <SpinnerIcon className="w-4 h-4" />
                Copying…
              </>
            ) : (
              'Copy folder'
            )}
          </Button>
        </div>
      </ModalFooter>
    </Modal>
  );
};

export default TemplateFolderCopyConfirmModal;
